import {
  ApiBlueprint,
  createApiFactory,
  createFrontendModule,
  createRouteRef,
  discoveryApiRef,
  fetchApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import MemoryIcon from '@material-ui/icons/Memory';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import LockIcon from '@material-ui/icons/Lock';
import FolderIcon from '@material-ui/icons/Folder';
import { VaultClient, vaultApiRef } from '../../components/vault/vaultApi';

const vaultApi = ApiBlueprint.make({
  name: 'vault',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: vaultApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          fetchApi: fetchApiRef,
        },
        factory: ({ discoveryApi, fetchApi }) =>
          new VaultClient(discoveryApi, fetchApi),
      }),
    ),
});

const llmProvidersRouteRef = createRouteRef();
const apiProvidersRouteRef = createRouteRef();
const credentialsRouteRef = createRouteRef();
const projectsRouteRef = createRouteRef();

const llmProvidersPage = PageBlueprint.make({
  name: 'llm-providers',
  params: {
    path: '/',
    title: 'LLM Providers',
    icon: <MemoryIcon fontSize="inherit" />,
    routeRef: llmProvidersRouteRef,
    noHeader: true,
    loader: async () => {
      const { VaultIndexPage } = await import(
        '../../components/vault/VaultIndexPage'
      );
      const { providerColumns } = await import(
        '../../components/vault/columns'
      );
      return (
        <VaultIndexPage
          title="LLM Providers"
          kind="resource"
          type="llm-provider"
          columns={providerColumns}
          emptyContent="No LLM providers yet. Use Create to add one."
        />
      );
    },
  },
});

const apiProvidersPage = PageBlueprint.make({
  name: 'api-providers',
  params: {
    path: '/api-providers',
    title: 'API Providers',
    icon: <CloudQueueIcon fontSize="inherit" />,
    routeRef: apiProvidersRouteRef,
    noHeader: true,
    loader: async () => {
      const { VaultIndexPage } = await import(
        '../../components/vault/VaultIndexPage'
      );
      const { providerColumns } = await import(
        '../../components/vault/columns'
      );
      return (
        <VaultIndexPage
          title="API Providers"
          kind="resource"
          type="api-provider"
          columns={providerColumns}
          emptyContent="No API providers yet. Use Create to add one."
        />
      );
    },
  },
});

const credentialsPage = PageBlueprint.make({
  name: 'credentials',
  params: {
    path: '/credentials',
    title: 'Credentials',
    icon: <LockIcon fontSize="inherit" />,
    routeRef: credentialsRouteRef,
    noHeader: true,
    loader: async () => {
      const { VaultIndexPage } = await import(
        '../../components/vault/VaultIndexPage'
      );
      const { credentialColumns } = await import(
        '../../components/vault/columns'
      );
      return (
        <VaultIndexPage
          title="Credentials"
          kind="resource"
          type="credential"
          columns={credentialColumns}
          emptyContent="No credential entries yet. Passwords live in Proton Pass."
        />
      );
    },
  },
});

const projectsPage = PageBlueprint.make({
  name: 'projects',
  params: {
    path: '/projects',
    title: 'Projects',
    icon: <FolderIcon fontSize="inherit" />,
    routeRef: projectsRouteRef,
    noHeader: true,
    loader: async () => {
      const { VaultIndexPage } = await import(
        '../../components/vault/VaultIndexPage'
      );
      const { projectColumns } = await import(
        '../../components/vault/columns'
      );
      return (
        <VaultIndexPage
          title="Projects"
          kind="component"
          type="project"
          columns={projectColumns}
          emptyContent="No projects yet. Use Create to add one."
        />
      );
    },
  },
});

export const vaultModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    vaultApi,
    llmProvidersPage,
    apiProvidersPage,
    credentialsPage,
    projectsPage,
  ],
});
